import React, { useState, useEffect } from 'react';
import { AlertTriangle, Info, Shield } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const LegalDisclaimerBox = ({ variant = 'full', className = '' }) => {
  const [disclaimer, setDisclaimer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDisclaimer();
  }, []);

  const fetchDisclaimer = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/legal-disclaimer`);
      if (response.ok) {
        const data = await response.json();
        setDisclaimer(data);
      }
    } catch (error) {
      console.error('Error fetching legal disclaimer:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 ${className}`}>
        <div className="animate-pulse h-4 bg-amber-200 dark:bg-amber-800 rounded w-3/4"></div>
      </div>
    );
  }

  if (!disclaimer) return null;

  // Compact variant for smaller spaces
  if (variant === 'compact') {
    return (
      <div className={`bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 ${className}`}>
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            {disclaimer.short_content || disclaimer.content}
          </p>
        </div>
      </div>
    );
  }

  // Full variant with all details
  return (
    <div className={`bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-5 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-800/50 rounded-full flex items-center justify-center">
          <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h4 className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {disclaimer.title}
          </h4>
        </div>
      </div>
      
      {/* Content */}
      <div className="space-y-3 text-sm text-amber-800 dark:text-amber-300">
        {disclaimer.content.split('\n\n').map((paragraph, index) => {
          // Check if paragraph contains bullet points
          if (paragraph.includes('•')) {
            const lines = paragraph.split('\n');
            return (
              <ul key={index} className="space-y-2 ml-1">
                {lines.map((line, lineIndex) => {
                  if (line.startsWith('•')) {
                    return (
                      <li key={lineIndex} className="flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        <span>{line.substring(1).trim()}</span>
                      </li>
                    );
                  }
                  return <span key={lineIndex}>{line}</span>;
                })}
              </ul>
            );
          }
          return <p key={index}>{paragraph}</p>;
        })}
      </div>
      
      {/* Footer note */}
      <div className="mt-4 pt-3 border-t border-amber-200 dark:border-amber-700">
        <p className="text-xs text-amber-600 dark:text-amber-500 flex items-center gap-1">
          <Info className="w-3 h-3" />
          This notice cannot be dismissed and applies to all transactions.
        </p>
      </div>
    </div>
  );
};

export default LegalDisclaimerBox;
